// Plan badge — port of the storybook repo's AntdPlanBadge.vue
// (stpancras-storybook-app/src/components/UI/badge/AntdPlanBadge.vue).
//
// Same keyword-based palette (emerald / indigo / coral / secondary / purple)
// mapped onto AntD's Tag, so badges in this embed match what the presenter
// renders. Tailwind classes from the Vue version are inlined here — this
// frontend doesn't pull in Tailwind, and AntD's Tag already handles the
// remaining markup / a11y. Tokens come from the storybook's tailwind.config.js.

import { Tag } from "antd";
import { getPlanDisplayName } from "../planNames";

export interface PlanBadgeProps {
  /** Raw plan key as returned by general-api (e.g. "pro-yearly", "edu-medium"). */
  plan: string | null | undefined;
  /** Enterprise override — renders "Enterprise" with the purple palette
   *  regardless of `plan`. Matches the Vue component's isEnterprise prop. */
  isEnterprise?: boolean;
}

interface PlanPalette {
  bg: string;
  text: string;
}

// Keyword → palette mapping. Order matters: more specific keywords (e.g.
// "essential") come before broader ones if there's any ambiguity. The plan
// key is matched lowercase, against the raw upstream value (so "edu-small"
// hits the essential row via "small", "edu-medium" hits plus via "medium",
// etc. — mirrors the Vue component).
//
// Values reference SEMANTIC tokens from src/tokens.css (--plan-badge-…), not
// primitive shades. To re-skin a tier, edit the semantic alias once in
// tokens.css; this file never sees a hex.
const PALETTES: ReadonlyArray<{ test: RegExp; palette: PlanPalette }> = [
  { test: /(medium|plus)/i, palette: { bg: "var(--plan-badge-plus-bg)", text: "var(--plan-badge-plus-text)" } },
  { test: /(small|essential)/i, palette: { bg: "var(--plan-badge-essential-bg)", text: "var(--plan-badge-essential-text)" } },
  { test: /(pro|large)/i, palette: { bg: "var(--plan-badge-pro-bg)", text: "var(--plan-badge-pro-text)" } },
  { test: /white\s*label/i, palette: { bg: "var(--plan-badge-white-label-bg)", text: "var(--plan-badge-white-label-text)" } },
];

const DEFAULT_PALETTE: PlanPalette = {
  bg: "var(--plan-badge-default-bg)",
  text: "var(--plan-badge-default-text)",
};

function pickPalette(plan: string | null | undefined, isEnterprise: boolean): PlanPalette {
  if (isEnterprise) return DEFAULT_PALETTE;
  if (!plan) return DEFAULT_PALETTE;
  for (const { test, palette } of PALETTES) {
    if (test.test(plan)) return palette;
  }
  return DEFAULT_PALETTE;
}

export function PlanBadge({ plan, isEnterprise = false }: PlanBadgeProps) {
  const label = isEnterprise ? "Enterprise" : getPlanDisplayName(plan);
  if (!label) return null;

  const { bg, text } = pickPalette(plan, isEnterprise);

  return (
    <Tag
      data-testid="plan-badge"
      // Inline style overrides AntD's preset Tag styling. AHA2-21: badge SIZE
      // cloned exactly from the presenter reference
      // (TeamLicenseUsageMeter.vue @ acc3d51): the plan badge is
      // `inline-flex h-6 items-center justify-center rounded-md px-3 text-sm
      // font-semibold` → height 24px, radius 6px (rounded-md), horizontal
      // padding 12px with NO vertical padding (fixed 24px box, text centred),
      // 14px / 600. (The per-plan colour palette + Enterprise label are this
      // repo's own product behaviour and are intentionally kept — the reporter
      // asked to match the badge SIZE, not recolour it.) marginInlineEnd: 0
      // cancels AntD's default 8px trailing gap so the parent flex `gap`
      // controls spacing.
      style={{
        backgroundColor: bg,
        color: text,
        border: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        height: 24,
        padding: "0 12px",
        borderRadius: 6,
        lineHeight: 1,
        fontWeight: 600,
        fontSize: 14,
        marginInlineEnd: 0,
      }}
    >
      {label}
    </Tag>
  );
}
